import { cn, initials } from "@/lib/utils";

const PALETTE = [
  "bg-orange-500",
  "bg-teal-600",
  "bg-indigo-600",
  "bg-rose-500",
  "bg-amber-600",
  "bg-cyan-700",
  "bg-emerald-600",
  "bg-fuchsia-600",
];

function colorForId(id: string): string {
  // `| 0` coerces to int32 so the hash is stable across JS runtimes; same org
  // always lands on the same slot. Random would flicker across page loads.
  const hash = [...id].reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) | 0, 0);
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

type Size = "sm" | "md" | "lg";

const SIZE_TO_CLASS: Record<Size, string> = {
  sm: "h-5 w-5 text-[10px]",
  md: "h-8 w-8 text-xs",
  lg: "h-16 w-16 text-2xl",
};

// Rounded-square (not circle) — intentional contrast with the circular user
// avatar elsewhere. Shape signals workspace vs person at a glance.
const SHAPE = "rounded-md";

type Props = {
  logoUrl: string | null | undefined;
  name: string;
  id: string;
  size?: Size;
  className?: string;
};

export function OrgAvatar({
  logoUrl,
  name,
  id,
  size = "sm",
  className,
}: Props) {
  const sizeClass = SIZE_TO_CLASS[size];
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className={cn(
          sizeClass,
          SHAPE,
          "object-contain bg-white flex-shrink-0",
          className,
        )}
      />
    );
  }
  return (
    <div
      aria-label={name}
      className={cn(
        sizeClass,
        SHAPE,
        "flex flex-shrink-0 items-center justify-center font-medium text-white",
        colorForId(id),
        className,
      )}
    >
      {initials(name)}
    </div>
  );
}
